// Scanner API service types and methods
import { API_BASE } from '../env';
import { apiFetch } from '../auth/apiClient';

// Scanner types matching backend models
export interface ScannerConfig {
  id: string;
  name: string;
  description: string;
  type: ScannerType;
  filters: ScannerFilter[];
  columns: ScannerColumn[];
  sort?: ScannerSortConfig;
  maxResults: number;
  isActive: boolean;
  isPreset: boolean;
  createdAt: string;
  lastUpdated?: string;
  strategies?: ScannerStrategy[];
  // Unified strategy fields (from merged StrategyConfig)
  alerts?: string[];           // AlertTypeCode strings (e.g. 'NHP', 'RUN')
  exchanges?: string[];        // e.g. ['NASDAQ', 'NYSE']
  symbolList?: string[];       // Restrict to specific symbols
  symbolListId?: string | null; // Owner-scoped named universe; backend resolves into symbolList
  category?: StrategyCategory; // Strategy grouping
  atrPeriod?: number;          // ATR lookback (default 14)
  smaPeriod?: number;          // SMA lookback (0 = disabled)
  alertAction?: string;        // e.g. 'Sound', 'Popup'
  entryMaxAgeMinutes?: number; // TTL for strategy entries (null = permanent)
  assetClass?: 'Equity' | 'Forex' | 'Crypto'; // which market this scanner screens (default Equity)
}

export type StrategyCategory =
  | 'Bullish'
  | 'Bearish'
  | 'Neutral'
  | 'PreMarket'
  | 'PostMarket'
  | 'Momentum'
  | 'Reversal'
  | 'HaltResume'
  | 'Custom';

/** Strategy assigned to a scanner, with display color */
export interface ScannerStrategy {
  strategyId: string;
  name: string;
  color: string;
  enabled: boolean;
}

export type ScannerType = 
  | 'MomoHOD'
  | 'MomoLOD'
  | 'GapUp'
  | 'GapDown'
  | 'MomoUp2Min'
  | 'MomoDown2Min'
  | 'VolatilityHalt'
  | 'NewsHalt'
  | 'VolumeSpike'
  | 'NewHighs'
  | 'NewLows'
  | 'PreMarketGainers'
  | 'PreMarketLosers'
  | 'TopGainers'
  | 'TopLosers'
  | 'MostActive'
  | 'Custom'
  // Pro Scanner types
  | 'OtcGappers'
  | 'HighestChangeFromOpen'
  | 'HaltResume'
  | 'PreMarketSmallUpGap'
  | 'PreMarketSmallDownGap'
  | 'PreMarketMidLargeGap'
  | 'HodMomoSmallCap'
  | 'HodMomoLargeCap'
  | 'HodMomoV2SmallCap'
  | 'LodMomoSmallCap'
  | 'LodMomoLargeCap'
  | 'PennyGainers'
  | 'PennyLosers'
  | 'OnFire'
  | 'BreakoutVolume'
  | 'OptionVolumePlays';

export interface ScannerFilter {
  field: string;
  operator: FilterOperator;
  value?: any;
  value2?: any;
}

export type FilterOperator = 
  | 'Equals'
  | 'NotEquals'
  | 'GreaterThan'
  | 'GreaterThanOrEqual'
  | 'LessThan'
  | 'LessThanOrEqual'
  | 'Between'
  | 'In'
  | 'NotIn'
  | 'Contains'
  | 'StartsWith'
  | 'EndsWith';

export interface ScannerColumn {
  field: string;
  header: string;
  width: number;
  format: ColumnFormat;
  visible: boolean;
  order: number;
}

export type ColumnFormat = 
  | 'Text'
  | 'Currency'
  | 'Percent'
  | 'Number'
  | 'Volume'
  | 'Time'
  | 'DateTime'
  | 'Change'
  | 'ChangePercent';

export interface ScannerSortConfig {
  field: string;
  descending: boolean;
}

/** One filter a detection cleared — field/comparison/threshold + the symbol's
 *  actual value at detection time. Populated only for passing detections so the
 *  genuineness of a match is inspectable and mis-configured filters are visible. */
export interface MatchAuditItem {
  field: string;
  operator: string;
  threshold: string | null;
  actualValue: string | null;
  passed: boolean;
}

export interface ScannerResult {
  symbol: string;
  name: string;
  exchange: string;
  price: number;
  open: number;
  high: number;
  low: number;
  prevClose: number;
  change: number;
  changePercent: number;
  change2Min: number;
  changePercent2Min: number;
  volume: number;
  avgVolume: number;
  relativeVolume: number;
  volumePercent: number;
  gapPercent: number;
  float: number;
  marketCap: number;
  vwap: number;
  atr: number;
  atrPercent: number;
  atrRoomPct: number;
  vwapSlopeAtr: number;
  atrSpd: number;
  atrVwap: number;
  atrHoD: number;
  sma: number;
  isHalted: boolean;
  haltReason?: string;
  // "News" | "Volatility" | "Regulatory" | "Other" — what separates the two halt scanners.
  haltKind?: string;
  // Minutes since resume; absent unless recently resumed. A resume clears isHalted, so this is
  // what keeps a resumed symbol visible to the Halt/Resume scanner.
  minutesSinceResume?: number;
  haltTime?: string;
  resTime?: string;
  resPrice?: number;
  hasNews: boolean;
  latestHeadline?: string;
  newsTime?: string;
  // LULD price bands — the pre-halt signal. Absent when the symbol has no band data;
  // never treat a missing band as 0, which would read as "pinned to the band".
  limitUpPrice?: number;
  limitDownPrice?: number;
  lastUpdate: string;
  /** First instant the symbol entered the matching set for its current stay (null on stateless paths). */
  detectedAt?: string | null;
  sector?: string;
  industry?: string;
  // Historical stats (from unified strategy engine)
  week52High: number;
  week52Low: number;
  dailySma20: number;
  dailySma50: number;
  dailySma200: number;
  isPreMarket: boolean;
  isPostMarket: boolean;
  /** Per-filter match audit — only present for passing detections (see MatchAuditItem). */
  matchAudit?: MatchAuditItem[] | null;
}

/**
 * Dynamic scanner update — contains only data that changes every evaluation cycle.
 * Static config (columns, sort, name, description) comes via ScannerMetadata once on subscribe.
 */
export interface ScannerUpdate {
  scannerId: string;
  results: ScannerResult[];
  timestamp: string;
  totalMatchingSymbols: number;
}

/**
 * One-time metadata sent when a client subscribes to a scanner.
 * Contains all static configuration that never changes between updates.
 */
export interface ScannerMetadata {
  scannerId: string;
  scannerName: string;
  description: string;
  type: ScannerType;
  columns: ScannerColumn[];
  sort?: ScannerSortConfig;
  maxResults: number;
  filters: ScannerFilter[];
  // Unified strategy fields
  alerts?: string[];           // AlertTypeCode strings
  category?: StrategyCategory;
}

export interface AlertConfig {
  id: string;
  name: string;
  type: AlertType;
  symbol?: string;
  scannerId?: string;
  conditions: AlertCondition[];
  action: AlertAction;
  isActive: boolean;
  createdAt: string;
  triggerCount: number;
  lastTriggered?: string;
}

export type AlertType = 
  | 'PriceAbove'
  | 'PriceBelow'
  | 'PriceChange'
  | 'PercentChange'
  | 'VolumeSpike'
  | 'TechnicalIndicator'
  | 'ScannerNew'
  | 'ScannerRemoved'
  | 'News'
  | 'Halt'
  | 'Custom';

export interface AlertCondition {
  field: string;
  operator: FilterOperator;
  value?: any;
}

export interface AlertAction {
  playSound: boolean;
  soundFile?: string;
  showPopup: boolean;
  sendEmail: boolean;
  emailAddress?: string;
  sendPush: boolean;
}

export interface AlertTrigger {
  id: string;
  alertId: string;
  alertName: string;
  symbol: string;
  message: string;
  price?: number;
  timestamp: string;
  isRead: boolean;
  data: Record<string, any>;
}

class ScannerApiService {
  // Get all scanners
  async getScanners(): Promise<ScannerConfig[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner`);
    if (!response.ok) throw new Error('Failed to get scanners');
    return response.json();
  }

  // Get preset scanners
  async getPresetScanners(): Promise<ScannerConfig[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/presets`);
    if (!response.ok) throw new Error('Failed to get preset scanners');
    return response.json();
  }

  // Get scanner by ID
  async getScanner(scannerId: string): Promise<ScannerConfig> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/${scannerId}`);
    if (!response.ok) throw new Error(`Failed to get scanner ${scannerId}`);
    return response.json();
  }

  // Create scanner
  async createScanner(config: Partial<ScannerConfig>): Promise<ScannerConfig> {
    const response = await apiFetch(`${API_BASE}/api/Scanner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });
    if (!response.ok) throw new Error('Failed to create scanner');
    return response.json();
  }

  // Update scanner
  async updateScanner(scannerId: string, config: Partial<ScannerConfig>): Promise<ScannerConfig> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/${scannerId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });
    if (!response.ok) throw new Error('Failed to update scanner');
    return response.json();
  }

  // Delete scanner
  async deleteScanner(scannerId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/${scannerId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete scanner');
  }

  // Run scanner and get results
  async runScanner(scannerId: string): Promise<ScannerUpdate> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/${scannerId}/run`);
    if (!response.ok) throw new Error(`Failed to run scanner ${scannerId}`);
    return response.json();
  }

  // Get scanner results
  async getScannerResults(scannerId: string): Promise<ScannerResult[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/${scannerId}/results`);
    if (!response.ok) throw new Error('Failed to get scanner results');
    return response.json();
  }

  // Get single symbol data
  async getSymbolData(symbol: string): Promise<ScannerResult> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/symbol/${symbol}`);
    if (!response.ok) throw new Error(`Failed to get data for ${symbol}`);
    return response.json();
  }

  // Get multiple symbols data
  async getMultipleSymbolData(symbols: string[]): Promise<ScannerResult[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/symbols`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(symbols)
    });
    if (!response.ok) throw new Error('Failed to get symbol data');
    return response.json();
  }

  // Get scanner types
  async getScannerTypes(): Promise<{ value: string; name: string; description: string }[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/types`);
    if (!response.ok) throw new Error('Failed to get scanner types');
    return response.json();
  }

  // Get filter fields
  async getFilterFields(): Promise<{ field: string; type: string; description: string }[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/fields`);
    if (!response.ok) throw new Error('Failed to get filter fields');
    return response.json();
  }

  // Alert methods
  async getAlerts(): Promise<AlertConfig[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/alerts`);
    if (!response.ok) throw new Error('Failed to get alerts');
    return response.json();
  }

  async createAlert(alert: Partial<AlertConfig>): Promise<AlertConfig> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/alerts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(alert)
    });
    if (!response.ok) throw new Error('Failed to create alert');
    return response.json();
  }

  async deleteAlert(alertId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/alerts/${alertId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete alert');
  }

  async getAlertHistory(count: number = 100): Promise<AlertTrigger[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/alerts/history?count=${count}`);
    if (!response.ok) throw new Error('Failed to get alert history');
    return response.json();
  }

  // ── Dashboard layout methods ──

  async getDashboards(): Promise<DashboardLayout[]> {
    const response = await apiFetch(`${API_BASE}/api/Dashboard`);
    if (!response.ok) throw new Error('Failed to get dashboards');
    return response.json();
  }

  async getDashboard(id: string): Promise<DashboardLayout> {
    const response = await apiFetch(`${API_BASE}/api/Dashboard/${id}`);
    if (!response.ok) throw new Error(`Failed to get dashboard ${id}`);
    return response.json();
  }

  async createDashboard(layout: Partial<DashboardLayout>): Promise<DashboardLayout> {
    const response = await apiFetch(`${API_BASE}/api/Dashboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(layout)
    });
    if (!response.ok) throw new Error('Failed to create dashboard');
    return response.json();
  }

  async updateDashboard(id: string, layout: Partial<DashboardLayout>): Promise<DashboardLayout> {
    const response = await apiFetch(`${API_BASE}/api/Dashboard/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(layout)
    });
    if (!response.ok) throw new Error('Failed to update dashboard');
    return response.json();
  }

  async deleteDashboard(id: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/api/Dashboard/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete dashboard');
  }
}

// ── Dashboard layout types ──

export interface DashboardLayout {
  id: string;
  name: string;
  description?: string;
  panels: DashboardPanel[];
  columns: number;
  isDefault: boolean;
  createdAt: string;
  lastUpdated?: string;
}

export interface DashboardPanel {
  scannerId: string;
  row: number;
  col: number;
  colSpan: number;
  rowSpan: number;
  headerColor?: string;
}

export const scannerApiService = new ScannerApiService();
export default scannerApiService;
